/**
 * The roadmap as the app reads it: the rows of `docs/roadmap/README.md`
 * (parsed by `src/docs/roadmapSync.ts`), joined to the areas of the
 * architecture map. Pure — the host sends rows, the Roadmap view and the
 * Architecture map derive everything else here, so the two cannot disagree
 * about what "landed" means.
 *
 * A row's `Area` cell is a repository path (`src/acp`), a comma-separated
 * list of them (`desktop, webview-ui, shared/protocol`), `(root)` for the
 * repository itself or `(all)` for everything. It meets an area of the map
 * when either path contains the other: a row on `src/acp` belongs to the
 * `src/acp/taskBoard` area and vice versa, since the roadmap and the map do
 * not draw their boxes at the same depth.
 */
// ── The Target column: a month, a quarter or a day ───────────────────────
/** A target as the file keeps it: `2026-11`, `2026-Q4` or `2026-11-30`. */
export const ROADMAP_TARGET_PATTERN = /^\d{4}-(0[1-9]|1[0-2])(-(0[1-9]|[12]\d|3[01]))?$|^\d{4}-Q[1-4]$/;
const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
const MONTH_WORDS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
/**
 * A Target cell as the file should keep it, or undefined for none: the three
 * written forms as they are, and what people type instead — `Q4 2026`,
 * `2026 Q4`, `Nov 2026`, `November 2026`, `2026/11` — put the right way
 * round. A dash, an empty cell or anything that is not a date is no target.
 */
export function normaliseRoadmapTarget(value) {
    const v = (value ?? "").trim().replace(/^`|`$/g, "");
    if (!v || v === "-" || v === "—")
        return undefined;
    if (ROADMAP_TARGET_PATTERN.test(v))
        return validDay(v) ? v : undefined;
    const q = /^(?:q([1-4])[\s/-]*(\d{4})|(\d{4})[\s/-]*q([1-4]))$/i.exec(v);
    if (q)
        return `${q[2] ?? q[3]}-Q${q[1] ?? q[4]}`;
    const ym = /^(\d{4})[/.](\d{1,2})$/.exec(v);
    if (ym && Number(ym[2]) >= 1 && Number(ym[2]) <= 12)
        return `${ym[1]}-${ym[2].padStart(2, "0")}`;
    const mw = /^([A-Za-z]{3,9})\.?\s+(\d{4})$/.exec(v);
    if (mw) {
        const at = MONTHS.indexOf(mw[1].slice(0, 3).toLowerCase());
        if (at >= 0)
            return `${mw[2]}-${String(at + 1).padStart(2, "0")}`;
    }
    return undefined;
}
function validDay(v) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
    if (!m)
        return true;
    const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
    return d.getUTCMonth() === Number(m[2]) - 1;
}
/** A target in words: `2026-11` → "Nov 2026"; `2026-Q4` → "Q4 2026"; a day → "30 Nov 2026". */
export function roadmapTargetWords(target) {
    const q = /^(\d{4})-Q([1-4])$/.exec(target);
    if (q)
        return `Q${q[2]} ${q[1]}`;
    const m = /^(\d{4})-(\d{2})(?:-(\d{2}))?$/.exec(target);
    if (!m)
        return target;
    const month = MONTH_WORDS[Number(m[2]) - 1] ?? m[2];
    return m[3] ? `${Number(m[3])} ${month} ${m[1]}` : `${month} ${m[1]}`;
}
/** The last day a target means, as an ISO day: a month's last day, a quarter's last day, or the day. */
export function roadmapTargetEnd(target) {
    const q = /^(\d{4})-Q([1-4])$/.exec(target);
    if (q)
        return new Date(Date.UTC(Number(q[1]), Number(q[2]) * 3, 0)).toISOString().slice(0, 10);
    const m = /^(\d{4})-(\d{2})$/.exec(target);
    if (m)
        return new Date(Date.UTC(Number(m[1]), Number(m[2]), 0)).toISOString().slice(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(target) && validDay(target) ? target : undefined;
}
export const ROADMAP_GROUPS = ["landed", "landing", "planned", "dropped"];
export const GROUP_LABELS = {
    landed: "Done",
    landing: "In progress",
    planned: "Planned",
    dropped: "Set aside",
};
export const STATUS_LABELS = {
    done: "Done",
    "in-progress": "In progress",
    "not-started": "Not started",
    deferred: "Deferred",
    "wont-do": "Won't do",
};
export function groupOf(status) {
    switch (status) {
        case "done":
            return "landed";
        case "in-progress":
            return "landing";
        case "not-started":
            return "planned";
        default:
            return "dropped";
    }
}
/** The area paths a row names, normalised: `(root)` becomes `""`, `(all)` becomes `"*"`. */
export function roadmapAreaPaths(area) {
    return area
        .split(",")
        .map((s) => s.trim().replace(/^`|`$/g, "").replace(/\/+$/, ""))
        .filter(Boolean)
        .map((s) => (s === "(root)" ? "" : s === "(all)" ? "*" : s));
}
/** True when a row on `rowPath` belongs on the map's `areaPath` (either contains the other). */
export function areaMatches(rowPath, areaPath) {
    if (rowPath === "*")
        return true;
    if (rowPath === "")
        return areaPath === "";
    if (rowPath === areaPath)
        return true;
    return areaPath.startsWith(`${rowPath}/`) || rowPath.startsWith(`${areaPath}/`);
}
/** The rows that belong on one area of the map, in table order. */
export function rowsForArea(rows, areaPath) {
    return rows.filter((r) => roadmapAreaPaths(r.area).some((p) => areaMatches(p, areaPath)));
}
export function countRows(rows) {
    const counts = { landed: 0, landing: 0, planned: 0, dropped: 0, total: rows.length };
    for (const r of rows)
        counts[groupOf(r.status)]++;
    return counts;
}
/**
 * The one word an area card shows: what the area is mostly about right now.
 * Anything still in progress or planned outranks what has landed, because
 * "this area has open work" is the thing worth a glance; an area with only
 * landed rows reads as settled; one with nothing on the roadmap says nothing.
 */
export function areaTone(counts) {
    if (counts.total === 0)
        return undefined;
    if (counts.landing > 0)
        return "landing";
    if (counts.planned > 0)
        return "planned";
    if (counts.landed > 0)
        return "landed";
    return "dropped";
}
/** The series a finding id belongs to: `S1` → `S`, `D29` → `D`, `O12` → `O`. */
export function seriesOf(id) {
    const m = /^([A-Za-z]+)/.exec(id.trim());
    return m ? m[1].toUpperCase() : "?";
}
/** What each series of ids in this repository's roadmap is about; unknown ones show their letter. */
export const SERIES_TITLES = {
    S: "Security",
    P: "Portability",
    X: "Resilience",
    C: "Context",
    T: "Teams",
    L: "Inference",
    M: "Memory",
    V: "Surfaces",
    U: "Interface",
    E: "Engineering",
    D: "Desktop",
    O: "Onboarding",
};
/** Commit SHAs the evidence names, for linking. */
export function evidenceShas(evidence) {
    return [...evidence.matchAll(/`([0-9a-f]{7,40})`/gi)].map((m) => m[1].toLowerCase());
}
// ── Sequencing: dependencies, blockers and the critical path ─────────────
/** A row that is finished or set aside no longer holds anything up. */
function settled(status) {
    return status === "done" || status === "wont-do" || status === "deferred";
}
/**
 * What waits on what. Pure, so the Roadmap view and the Architecture map say
 * the same thing. The critical path counts unfinished items: the chain where
 * one more slip delays the end.
 */
export function roadmapDependencies(items) {
    const byId = new Map(items.map((i) => [i.id, i]));
    const blockedBy = new Map();
    const blocking = new Map();
    const unknown = new Map();
    for (const item of items) {
        const deps = item.dependsOn ?? [];
        const missing = deps.filter((d) => !byId.has(d));
        if (missing.length)
            unknown.set(item.id, missing);
        if (settled(item.status))
            continue;
        const open = deps.filter((d) => byId.has(d) && !settled(byId.get(d).status));
        if (open.length)
            blockedBy.set(item.id, open);
        for (const d of open)
            blocking.set(d, [...(blocking.get(d) ?? []), item.id]);
    }
    // Loops: a depth-first walk over every dependency, finished or not.
    const cycles = [];
    const state = new Map();
    const stack = [];
    const seenLoop = new Set();
    const visit = (id) => {
        state.set(id, "visiting");
        stack.push(id);
        for (const d of byId.get(id)?.dependsOn ?? []) {
            if (!byId.has(d))
                continue;
            if (state.get(d) === "visiting") {
                const loop = stack.slice(stack.indexOf(d));
                const key = [...loop].sort().join(",");
                if (!seenLoop.has(key)) {
                    seenLoop.add(key);
                    cycles.push(loop);
                }
            }
            else if (!state.has(d))
                visit(d);
        }
        stack.pop();
        state.set(id, "done");
    };
    for (const i of items)
        if (!state.has(i.id))
            visit(i.id);
    // The critical path: the longest chain through unfinished items, ignoring loops.
    const inLoop = new Set(cycles.flat());
    const memo = new Map();
    const longest = (id, seen) => {
        const cached = memo.get(id);
        if (cached)
            return cached;
        let best = [];
        for (const d of blockedBy.get(id) ?? []) {
            if (seen.has(d) || inLoop.has(d))
                continue;
            const chain = longest(d, new Set([...seen, d]));
            if (chain.length > best.length)
                best = chain;
        }
        const out = [...best, id];
        memo.set(id, out);
        return out;
    };
    let criticalPath = [];
    for (const item of items) {
        if (settled(item.status) || inLoop.has(item.id))
            continue;
        const chain = longest(item.id, new Set([item.id]));
        if (chain.length > criticalPath.length)
            criticalPath = chain;
    }
    if (criticalPath.length < 2)
        criticalPath = [];
    return { blockedBy, blocking, criticalPath, unknown, cycles };
}
//# sourceMappingURL=model.js.map